/**
 * SectionCanvas — a continuous scrollable canvas of grouped sections with a
 * sticky progress rail, scroll-spy, and focus mode. Module-agnostic: the
 * Plot canvas configures it with acts/beats; the Character canvas (PR 3)
 * reuses it with levels/questions.
 *
 * Behaviour contract:
 *  - Scroll-spy: a section is "active" when it enters the 35–45% viewport
 *    band (IntersectionObserver, rootMargin -35%/-55%); onActiveChange fires
 *    DEBOUNCED (150ms) so coaching surfaces attach to the section being
 *    read, not every section skimmed past.
 *  - Rail: sticky left column on wide containers; collapses to a sticky,
 *    horizontally-scrollable top strip when the CONTAINER (not viewport) is
 *    narrower than 700px — split-view panes behave like phones.
 *  - Focus mode: isolate one section; the caller persists the preference.
 *  - Rail clicks smooth-scroll; initialSectionId scrolls on mount.
 *
 * Progress signals (restraint by design — reward depth, never box-filling):
 *  - Three-state dots per rail item: hollow (empty) / half (in progress) /
 *    solid amber (developed) — driven by section.dev from lib/devLevel.js.
 *  - Substance ribbon: a slim track beside the rail whose segments mirror
 *    each section's depth; the active section's segment brightens, so the
 *    rail position also reads as "how much of this module has substance".
 *  - Milestone marks: a group whose dev === 'developed' gets a small amber
 *    mark on its header plus a one-line acknowledgment (group.milestoneText).
 *    No modals, no confetti, no percentages anywhere.
 *
 * Props:
 *  groups: [{ id, title, subtitle?, banner?, footer?, dev?, milestoneText?,
 *             sections: [{ id, label, dev|complete, render }] }]
 *  (banner renders before the group's sections, footer after — coaching and
 *   supplements go in footers so writing stays first)
 *  onActiveChange(sectionId, groupId)  — debounced scroll-spy callback
 *  focusId / onFocusChange(idOrNull)   — focus mode (controlled)
 *  initialSectionId                    — scroll target on mount
 *  railFooter                          — optional node under the rail list
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Crosshair, X } from 'lucide-react';

export const NARROW_CONTAINER_PX = 700;
export const SPY_DEBOUNCE_MS = 150;

const AMBER = '#E8A020';

/** 'developed' | 'progress' | 'empty' from a section, tolerating the older
 *  boolean `complete` shape (PR 2 callers). */
const devOf = (section) =>
  section.dev ?? (section.complete ? 'developed' : 'empty');

/** The three-state dot. Hollow = empty, half = in progress, solid = developed. */
function DevDot({ dev, testId }) {
  const style =
    dev === 'developed'
      ? { background: AMBER, borderColor: AMBER }
      : dev === 'progress'
        ? { background: `linear-gradient(90deg, ${AMBER} 50%, transparent 50%)`, borderColor: AMBER }
        : undefined;
  return (
    <span
      aria-hidden
      data-testid={testId}
      data-dev={dev}
      className="w-2 h-2 flex-shrink-0 border border-border"
      style={style}
    />
  );
}

// Hoisted so the rail buttons keep their identity across parent re-renders
// (defining it inline remounted the whole rail on every keystroke).
function RailItem({ section, narrow, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`rail-${section.id}`}
      className={`flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider transition-colors text-left ${
        narrow ? 'px-2.5 py-2 flex-shrink-0' : 'w-full px-3 py-1.5'
      } ${
        active
          ? 'text-primary bg-primary/10 border-l-2 border-primary'
          : 'text-muted-foreground hover:text-foreground border-l-2 border-transparent'
      }`}
    >
      <DevDot dev={devOf(section)} testId={`dot-${section.id}`} />
      <span className={narrow ? 'whitespace-nowrap' : 'truncate'}>{section.label}</span>
    </button>
  );
}

/** Substance ribbon — one slim segment per section, in document order. The
 *  position indicator and the depth signal are the same element: the active
 *  segment renders at full strength, developed segments amber, in-progress
 *  dim amber, empty near-invisible. */
function SubstanceRibbon({ sections, activeId, narrow, onJump }) {
  return (
    <div
      data-testid="substance-ribbon"
      aria-hidden
      className={narrow ? 'flex h-0.5 w-full gap-px' : 'flex flex-col w-0.5 self-stretch gap-px mr-1.5'}
    >
      {sections.map((s) => {
        const dev = devOf(s);
        const isActive = s.id === activeId;
        return (
          <div
            key={s.id}
            onClick={() => onJump?.(s.id)}
            data-dev={dev}
            className={`${narrow ? 'flex-1 h-full' : 'flex-1 w-full'} cursor-pointer transition-opacity`}
            style={{
              background: dev === 'empty' ? 'var(--border, #333)' : AMBER,
              opacity: isActive ? 1 : dev === 'developed' ? 0.85 : dev === 'progress' ? 0.4 : 0.25,
              outline: isActive ? `1px solid ${AMBER}` : 'none',
            }}
          />
        );
      })}
    </div>
  );
}

export default function SectionCanvas({
  groups,
  onActiveChange,
  focusId = null,
  onFocusChange,
  initialSectionId = null,
  railFooter = null,
}) {
  const containerRef = useRef(null);
  const sectionRefs = useRef(new Map()); // sectionId -> element
  const [activeId, setActiveId] = useState(null);
  const [narrow, setNarrow] = useState(false);
  // The workspace header is sticky top-0 z-40 and its height varies by
  // viewport — measure it so the strip/banner stick BELOW it instead of
  // underneath it (fixed offsets drift when the header wraps).
  const [headerOffset, setHeaderOffset] = useState(0);
  const debounceRef = useRef(null);
  const onActiveChangeRef = useRef(onActiveChange);
  onActiveChangeRef.current = onActiveChange;

  useEffect(() => {
    const measure = () => {
      const header = document.querySelector('header');
      setHeaderOffset(header ? Math.ceil(header.getBoundingClientRect().height) : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const flatSections = useMemo(
    () => groups.flatMap((g) => g.sections.map((s) => ({ ...s, groupId: g.id }))),
    [groups],
  );
  // Callers rebuild `groups` every render (their section renderers close
  // over live form state), so the observer must key on the SET of section
  // ids — not object identity — or it tears down on every keystroke.
  const sectionIdsKey = flatSections.map((s) => s.id).join('|');
  const flatSectionsRef = useRef(flatSections);
  flatSectionsRef.current = flatSections;

  const registerSection = useCallback((id) => (el) => {
    if (el) sectionRefs.current.set(id, el);
    else sectionRefs.current.delete(id);
  }, []);

  // ── Rail collapse: watch the CONTAINER width (split panes behave) ──
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? el.clientWidth;
      setNarrow(width < NARROW_CONTAINER_PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Scroll-spy: active = the section in the 35–45% viewport band ──
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const visible = new Set();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const id = e.target.getAttribute('data-section-id');
          if (e.isIntersecting) visible.add(id);
          else visible.delete(id);
        });
        // Of the sections crossing the band, take the first in document
        // order — during a fast scroll several flicker through; the
        // debounce below keeps coaching from thrashing.
        const first = flatSectionsRef.current.find((s) => visible.has(s.id));
        if (!first) return;
        setActiveId(first.id);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          onActiveChangeRef.current?.(first.id, first.groupId);
        }, SPY_DEBOUNCE_MS);
      },
      { rootMargin: '-35% 0px -55% 0px', threshold: 0 },
    );
    sectionRefs.current.forEach((el) => io.observe(el));
    return () => {
      clearTimeout(debounceRef.current);
      io.disconnect();
    };
    // Re-observe only when the SET of rendered sections changes (id list or
    // focus filter) — content updates keep the live observer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIdsKey, focusId]);

  const scrollToSection = useCallback((id) => {
    const el = sectionRefs.current.get(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Initial deep-link scroll (route shim / "Build Scenes" return).
  const initialDoneRef = useRef(false);
  useEffect(() => {
    if (initialDoneRef.current || !initialSectionId) return;
    const el = sectionRefs.current.get(initialSectionId);
    if (el) {
      initialDoneRef.current = true;
      // Let layout settle before jumping (fonts/textareas expand on mount).
      requestAnimationFrame(() => el.scrollIntoView({ block: 'start' }));
    } else if (flatSections.length > 0) {
      // The target exists but is hidden (persisted focus mode). Consume the
      // deep link instead of ambushing the writer with a jump the moment
      // they exit focus, minutes later.
      initialDoneRef.current = true;
    }
  }, [initialSectionId, flatSections]);

  const visibleGroups = focusId
    ? groups
        .map((g) => ({ ...g, sections: g.sections.filter((s) => s.id === focusId) }))
        .filter((g) => g.sections.length > 0)
    : groups;

  const rail = (
    <nav
      data-testid="canvas-rail"
      aria-label="Sections"
      style={narrow ? { top: headerOffset } : undefined}
      className={
        narrow
          ? 'sticky z-20 -mx-1 px-1 bg-background/95 backdrop-blur border-b border-border/40 overflow-x-auto py-1'
          : 'w-48 flex-shrink-0'
      }
    >
      {narrow && (
        <SubstanceRibbon sections={flatSections} activeId={activeId} narrow onJump={scrollToSection} />
      )}
      <div className={narrow ? 'flex items-stretch gap-1' : 'sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2 flex'}>
        {!narrow && (
          <SubstanceRibbon sections={flatSections} activeId={activeId} narrow={false} onJump={scrollToSection} />
        )}
        <div className={narrow ? 'flex items-stretch gap-1' : 'flex-1 min-w-0 space-y-4'}>
          {groups.map((g) => (
            <div key={g.id} className={narrow ? 'flex items-stretch gap-1' : ''}>
              {!narrow && (
                <div className="px-3 mb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60 flex items-center gap-1.5">
                  {g.title}
                  {g.dev === 'developed' && (
                    <span aria-hidden className="w-1.5 h-1.5" style={{ background: AMBER }} />
                  )}
                </div>
              )}
              {g.sections.map((s) => (
                <RailItem
                  key={s.id}
                  section={s}
                  narrow={narrow}
                  active={activeId === s.id}
                  onClick={() => scrollToSection(s.id)}
                />
              ))}
            </div>
          ))}
          {!narrow && railFooter}
        </div>
      </div>
    </nav>
  );

  return (
    <div ref={containerRef} data-testid="section-canvas" className={narrow ? '' : 'flex gap-6'}>
      {rail}
      <div className="flex-1 min-w-0">
        {focusId && (
          <div
            style={{ top: headerOffset + (narrow ? 62 : 8) }}
            className="sticky z-10 mb-4 flex items-center justify-between bg-primary/10 border border-primary/30 px-3 py-2"
          >
            <span className="font-mono text-[11px] uppercase tracking-widest text-primary flex items-center gap-2">
              <Crosshair className="w-3.5 h-3.5" /> Focus mode
            </span>
            <button
              type="button"
              onClick={() => onFocusChange?.(null)}
              data-testid="focus-exit"
              className="font-mono text-[11px] uppercase tracking-widest text-primary hover:text-foreground flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Show all
            </button>
          </div>
        )}
        {visibleGroups.map((g) => (
          <section key={g.id} className="mb-12" data-testid={`canvas-group-${g.id}`}>
            <div className="mb-4 border-b border-border/40 pb-2">
              <h3 className="font-mono text-lg font-semibold tracking-tight flex items-center gap-2">
                {g.title}
                {g.dev === 'developed' && (
                  <span
                    aria-hidden
                    data-testid={`milestone-${g.id}`}
                    className="w-2 h-2 flex-shrink-0"
                    style={{ background: AMBER }}
                  />
                )}
              </h3>
              {g.subtitle && <p className="text-sm text-muted-foreground">{g.subtitle}</p>}
              {g.dev === 'developed' && g.milestoneText && (
                <p
                  data-testid={`milestone-text-${g.id}`}
                  className="mt-1 font-mono text-[11px] uppercase tracking-widest"
                  style={{ color: AMBER, opacity: 0.8 }}
                >
                  {g.milestoneText}
                </p>
              )}
            </div>
            {g.banner}
            {g.sections.map((s) => (
              <div
                key={s.id}
                ref={registerSection(s.id)}
                data-section-id={s.id}
                data-testid={`canvas-section-${s.id}`}
                style={{ scrollMarginTop: headerOffset + (narrow ? 70 : 16) }}
                className="mb-10"
              >
                {s.render({
                  active: activeId === s.id,
                  focused: focusId === s.id,
                  onFocus: () => onFocusChange?.(focusId === s.id ? null : s.id),
                })}
              </div>
            ))}
            {g.footer}
          </section>
        ))}
      </div>
    </div>
  );
}
